
import React from "react";

// ========================================
// PRODUCT APPLICATION METHOD
// ========================================

const ProductApplicationMethod = ({
    data = {},
    onChange
}) => {

    // ========================================
    // DATA
    // ========================================

    const applicationMethod = {
        surfacePreparation: "",
        materialsRequired: [],
        mixingInstructions: "",
        steps: [],
        curingInstructions: "",
        precautions: [],
        ...(data || {})
    };

    // ========================================
    // STYLES
    // ========================================

    const inputClass =
        "w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300";

    const labelClass =
        "block text-sm font-medium text-gray-700 mb-2";

    const addButtonClass =
        "px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700";

    const removeButtonClass =
        "px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm rounded hover:bg-red-100";

    // ========================================
    // UPDATE FIELD
    // ========================================

    const updateField = (field, value) => {

        onChange({
            ...applicationMethod,
            [field]: value
        });

    };

    // ========================================
    // STRING ARRAY MANAGEMENT
    // ========================================

    const getList = (field) => {

        return Array.isArray(applicationMethod[field])
            ? applicationMethod[field]
            : [];

    };

    const addListItem = (field) => {

        updateField(
            field,
            [
                ...getList(field),
                ""
            ]
        );

    };

    const updateListItem = (
        field,
        index,
        value
    ) => {

        updateField(
            field,
            getList(field).map(
                (item, i) =>
                    i === index
                        ? value
                        : item
            )
        );

    };

    const removeListItem = (
        field,
        index
    ) => {

        updateField(
            field,
            getList(field).filter(
                (_, i) => i !== index
            )
        );

    };

    // ========================================
    // APPLICATION STEPS
    // ========================================

    const steps = Array.isArray(applicationMethod.steps)
        ? applicationMethod.steps
        : [];

    const addStep = () => {

        const nextOrder = steps.reduce(
            (max, step) =>
                Math.max(max, Number(step.order) || 0),
            0
        ) + 1;

        updateField("steps", [
            ...steps,
            {
                title: "",
                instructions: "",
                order: nextOrder
            }
        ]);

    };

    const updateStep = (
        index,
        field,
        value
    ) => {

        updateField(
            "steps",
            steps.map(
                (step, i) =>
                    i === index
                        ? {
                            ...step,
                            [field]: value
                        }
                        : step
            )
        );

    };

    const removeStep = (index) => {

        updateField(
            "steps",
            steps.filter(
                (_, i) => i !== index
            )
        );

    };

    const moveStep = (index, direction) => {

        const targetIndex = index + direction;

        if (
            targetIndex < 0 ||
            targetIndex >= steps.length
        ) {
            return;
        }

        const updatedSteps = [...steps];

        [
            updatedSteps[index],
            updatedSteps[targetIndex]
        ] = [
            updatedSteps[targetIndex],
            updatedSteps[index]
        ];

        updateField(
            "steps",
            updatedSteps.map((step, i) => ({
                ...step,
                order: i + 1
            }))
        );

    };

    // ========================================
    // REUSABLE STRING LIST
    // ========================================

    const renderListEditor = (
        field,
        title,
        description,
        placeholder,
        addLabel
    ) => {

        const items = getList(field);

        return (

            <section className="space-y-4">

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 border-b pb-3">

                    <div>

                        <h3 className="text-base font-semibold text-gray-900">
                            {title}
                        </h3>

                        <p className="text-sm text-gray-500 mt-1">
                            {description}
                        </p>

                    </div>

                    <button
                        type="button"
                        onClick={() => addListItem(field)}
                        className={addButtonClass}
                    >
                        + {addLabel}
                    </button>

                </div>

                {items.length === 0 && (

                    <p className="text-sm text-gray-500">
                        No entries added yet.
                    </p>

                )}

                {items.map((item, index) => (

                    <div
                        key={index}
                        className="flex flex-col sm:flex-row gap-3"
                    >

                        <div className="flex-1">

                            <textarea
                                value={item || ""}
                                onChange={(event) =>
                                    updateListItem(
                                        field,
                                        index,
                                        event.target.value
                                    )
                                }
                                placeholder={placeholder}
                                rows={2}
                                className={inputClass}
                            />

                        </div>

                        <div className="sm:self-end">

                            <button
                                type="button"
                                onClick={() =>
                                    removeListItem(field, index)
                                }
                                className={removeButtonClass}
                            >
                                Remove
                            </button>

                        </div>

                    </div>

                ))}

            </section>

        );

    };

    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="space-y-10">

            {/* SURFACE PREPARATION */}

            <section className="space-y-4">

                <h3 className="text-base font-semibold text-gray-900 border-b pb-3">
                    Surface Preparation
                </h3>

                <p className="text-sm text-gray-500">
                    Describe the required preparation before
                    applying the product.
                </p>

                <textarea
                    value={applicationMethod.surfacePreparation || ""}
                    onChange={(event) =>
                        updateField(
                            "surfacePreparation",
                            event.target.value
                        )
                    }
                    placeholder="Describe cleaning, substrate preparation, moisture conditions and other preparation requirements."
                    rows={5}
                    className={inputClass}
                />

            </section>

            {/* MATERIALS REQUIRED */}

            {renderListEditor(
                "materialsRequired",
                "Materials Required",
                "List the materials, equipment or tools required for application.",
                "e.g. Mixing equipment or application tools specified by the manufacturer.",
                "Material"
            )}

            {/* MIXING INSTRUCTIONS */}

            <section className="space-y-4">

                <h3 className="text-base font-semibold text-gray-900 border-b pb-3">
                    Mixing Instructions
                </h3>

                <p className="text-sm text-gray-500">
                    Enter the manufacturer's approved mixing
                    procedure, where applicable.
                </p>

                <textarea
                    value={applicationMethod.mixingInstructions || ""}
                    onChange={(event) =>
                        updateField(
                            "mixingInstructions",
                            event.target.value
                        )
                    }
                    placeholder="Describe mixing ratios, mixing sequence, duration and any restrictions."
                    rows={5}
                    className={inputClass}
                />

            </section>

            {/* APPLICATION STEPS */}

            <section className="space-y-5">

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 border-b pb-3">

                    <div>

                        <h3 className="text-base font-semibold text-gray-900">
                            Application Steps
                        </h3>

                        <p className="text-sm text-gray-500 mt-1">
                            Define the application procedure
                            in the correct sequence.
                        </p>

                    </div>

                    <button
                        type="button"
                        onClick={addStep}
                        className={addButtonClass}
                    >
                        + Add Step
                    </button>

                </div>

                {steps.length === 0 && (

                    <div className="border border-dashed rounded-lg p-8 text-center">

                        <p className="text-sm text-gray-500">
                            No application steps added yet.
                        </p>

                    </div>

                )}

                {steps.map((step, index) => (

                    <div
                        key={step._id || index}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                    >

                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-gray-100 px-5 py-4">

                            <h4 className="font-semibold text-gray-900">
                                Step {index + 1}
                            </h4>

                            <div className="flex flex-wrap items-center gap-2">

                                <button
                                    type="button"
                                    onClick={() => moveStep(index, -1)}
                                    disabled={index === 0}
                                    className="px-3 py-2 border rounded text-sm bg-white disabled:opacity-40"
                                >
                                    ↑ Up
                                </button>

                                <button
                                    type="button"
                                    onClick={() => moveStep(index, 1)}
                                    disabled={index === steps.length - 1}
                                    className="px-3 py-2 border rounded text-sm bg-white disabled:opacity-40"
                                >
                                    ↓ Down
                                </button>

                                <button
                                    type="button"
                                    onClick={() => removeStep(index)}
                                    className={removeButtonClass}
                                >
                                    Remove
                                </button>

                            </div>

                        </div>

                        <div className="p-5 space-y-5">

                            <div>

                                <label className={labelClass}>
                                    Step Title *
                                </label>

                                <input
                                    type="text"
                                    value={step.title || ""}
                                    onChange={(event) =>
                                        updateStep(
                                            index,
                                            "title",
                                            event.target.value
                                        )
                                    }
                                    placeholder="e.g. Apply First Coat"
                                    className={inputClass}
                                />

                            </div>

                            <div>

                                <label className={labelClass}>
                                    Instructions *
                                </label>

                                <textarea
                                    value={step.instructions || ""}
                                    onChange={(event) =>
                                        updateStep(
                                            index,
                                            "instructions",
                                            event.target.value
                                        )
                                    }
                                    placeholder="Describe exactly how this step should be performed."
                                    rows={4}
                                    className={inputClass}
                                />

                            </div>

                            <div className="max-w-xs">

                                <label className={labelClass}>
                                    Step Order
                                </label>

                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={step.order ?? ""}
                                    onChange={(event) =>
                                        updateStep(
                                            index,
                                            "order",
                                            event.target.value === ""
                                                ? null
                                                : Number(event.target.value)
                                        )
                                    }
                                    className={inputClass}
                                />

                            </div>

                        </div>

                    </div>

                ))}

            </section>

            {/* CURING INSTRUCTIONS */}

            <section className="space-y-4">

                <h3 className="text-base font-semibold text-gray-900 border-b pb-3">
                    Curing Instructions
                </h3>

                <textarea
                    value={applicationMethod.curingInstructions || ""}
                    onChange={(event) =>
                        updateField(
                            "curingInstructions",
                            event.target.value
                        )
                    }
                    placeholder="Describe curing time, curing conditions and precautions during curing."
                    rows={5}
                    className={inputClass}
                />

            </section>

            {/* PRECAUTIONS */}

            {renderListEditor(
                "precautions",
                "Application Precautions",
                "Record important safety, environmental and application precautions.",
                "e.g. Follow the manufacturer's specified application conditions and protective equipment requirements.",
                "Precaution"
            )}

            <div className="border-t pt-4">

                <p className="text-xs text-gray-500">
                    Application procedures should come from
                    approved manufacturer instructions. The AI
                    agent should not invent mixing ratios,
                    application thicknesses, curing periods or
                    safety requirements.
                </p>

            </div>

        </div>

    );

};

export default ProductApplicationMethod;
